import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import '@oneclick/react-videolesson/dist/style.css';
import '@oneclick/tangerine-viewer/index.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { clientIdGoogle, DEFAULT_LANG } from 'app/config/config';
import SyncProgressContainer from 'core/modules/components/SyncProgress';
import { getCurrentLanguage } from 'core/utils/languages';
import { gapi } from 'gapi-script';
import { createBrowserHistory } from 'history';
import moment from 'moment';
import { PostHogProvider } from 'posthog-js/react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Switch } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { ToastProvider } from 'react-toast-notifications';
import { PersistGate } from 'redux-persist/integration/react';
import AIChatbot from './app/components/chatbot';
import InitsContainer from './app/containers/InitsContainer';
import SocketsContainer from './app/containers/SocketsContainer';
import TranslationsProvider from './app/providers/TranslationsProvider';
import { Routes } from './app/router/Routes';
import { RoutesEdelvives } from './edelvives/router/Routes';

import { isEdelvives } from './app/utils/utils';
import { I18nProvider, LayoutSplashScreen } from './core';
import ToastCustom from './core/modules/atoms/Toast';
import DialogGlobalPost from './core/modules/components/dialogs/DialogGlobalPost/DialogGlobalPost';
import DialogInfo from './core/modules/components/dialogs/DialogInfo/DialogInfo';
import analyticsService from './core/services/analyticsService';
import { msalConfig } from './edelvives/config/authConfig';

if (isEdelvives()) {
  import('./edelvives/utils/skinAssets/icons-gradient');
  import('./edelvives/utils/skinAssets/images');
} else {
  import('./app/utils/skinAssets/icons');
  import('./app/utils/skinAssets/icons-gradient');
  import('./app/utils/skinAssets/images');
}

const msalInstance = new PublicClientApplication(msalConfig);
const history = createBrowserHistory();



export default function App({ store, persistor, basename }) {
  const lang = getCurrentLanguage() === 'vl' ? 'ca' : getCurrentLanguage() || DEFAULT_LANG;

  moment.locale(lang);
  // Initialize Analytics Service
  useEffect(() => {
    try {
      analyticsService.initialize({
        // eslint-disable-next-line no-undef
        endpoint: process.env.REACT_APP_ANALYTICS_ENDPOINT,
      });
    } catch (error) {
      console.error('Failed to initialize analytics service:', error);
    }
  }, []);

  gapi.load('client:auth2', () => {
    gapi.auth2.init({
      clientId: clientIdGoogle[window.location.origin],
      plugin_name: 'chat',
    });
  });


  const appContent = (
    <GoogleOAuthProvider clientId={clientIdGoogle[window.location.origin]}>
      {/* Provide Redux store */}
      <Provider store={store}>
        <PostHogProvider
          // eslint-disable-next-line no-undef
          apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
          options={{
            // eslint-disable-next-line no-undef
            api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
            // disable_session_recording: true,
          }}
        >
          {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
          <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
            {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
            <React.Suspense fallback={<LayoutSplashScreen />}>
              {/* Override `basename` (e.g: `homepage` in `package.json`) */}
              <BrowserRouter basename={basename}>
                {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
                <LastLocationProvider>
                  {/* Provide Metronic theme overrides. */}
                  {/*<ThemeProvider>*/}
                  {/* Provide `react-intl` context synchronized with Redux state.  */}
                  <I18nProvider>
                    {/* Load translations based on tenant */}
                    <TranslationsProvider>
                      {/* Provide `react-toast-notifications` which provides context for the Toast descendants  */}
                      <DialogGlobalPost />
                      <DialogInfo />

                      <ToastProvider placement="bottom-left" components={{ Toast: ToastCustom }}>
                        {/* Render routes with provided `Layout`. */}
                        <AIChatbot>
                          <Switch>{isEdelvives() ? <RoutesEdelvives history={history} /> : <Routes history={history} />}</Switch>
                        </AIChatbot>
                        <SocketsContainer />
                        <InitsContainer />
                        <SyncProgressContainer />
                      </ToastProvider>
                    </TranslationsProvider>
                  </I18nProvider>
                  {/*</ThemeProvider>*/}
                </LastLocationProvider>
              </BrowserRouter>
            </React.Suspense>
          </PersistGate>
        </PostHogProvider>
      </Provider>
    </GoogleOAuthProvider>
  );

  // Solo envolver con MsalProvider si es Edelvives y msalInstance está disponible
  if (isEdelvives() && msalInstance) {
    return <MsalProvider instance={msalInstance}>{appContent}</MsalProvider>;
  }

  return appContent;
}

App.propTypes = {
  basename: PropTypes.string,
  persistor: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
};
